<template>
  <div class="container mt-5">
    <div class="row">
      <div class="col-md-6">
        <div class="mb-3">
          <label for="monthlyIncome">รายได้ต่อเดือน (รวมรายได้ผู้กู้ร่วม ถ้ามี)</label>
          <input v-model="monthlyIncome" @input="formatInput" class="form-control" type="text" id="monthlyIncome" />
        </div>

        <div class="mb-3">
          <label for="existingDebt">ภาระหนี้ที่ต้องผ่อนชำระต่อเดือน (ถ้ามี)</label>
          <input v-model.number="existingDebt" class="form-control" type="number" id="existingDebt" />
        </div>

        <div class="mb-3">
          <label for="loanTerm">ระยะเวลาที่ขอกู้ (ปี)</label>
          <input v-model.number="loanTerm" class="form-control" type="number" id="loanTerm" />
          <small class="text-secondary">*สูงสุด 30 ปี</small>
        </div>

        <div v-if="errorMessage" class="text-danger mt-1">
          <p>{{ errorMessage }}</p>
        </div>

        <div class="d-flex justify-content-end">
          <button class="btn btn-outline-secondary btn-start-cal mb-3 w-50 m-2" type="button"
            @click="StartcalculateLoanAmount">เริ่มใหม่</button>
          <button class="btn btn-primary mb-3 w-50 m-2" type="button" @click="calculateLoanAmount">คำนวณ</button>
        </div>
      </div>

      <div class="col-md-6 align-self-center">
        <div class="card card-cal mb-3 text-white">
          <div class="text-center ">
            <h2 class="fw-bold mb-3">ผลการคำนวณ</h2>
            <p>วงเงินกู้เงินโดยเฉลี่ยได้สูงสุด บาท</p>
            <h2 class="fw-bold result-number" :class="{ pop: animating }">
              {{ formatWithComma(displayLoanAmount.toFixed(0)) }} บาท*
            </h2>
          </div>
        </div>
      </div>
    </div>

    <div class="p-5 mb-3">
      <div class="fw-bold">หมายเหตุ</div>
      <div class="text-secondary"><small>- อัตราดอกเบี้ยที่ใช้ในการคำนวณ คือ อัตราดอกเบี้ย 5 %
          เป็นอัตราดอกเบี้ยเบื้องต้น</small></div>
      <div class="text-secondary"><small>- การคำนวณการกู้สินเชื่อเป็นเพียงการคำนวณแค่เบื้องต้นเท่านั้น
          ทั้งนี้ขึ้นอยู่กับการอนุมัติสินเชื่อตามหลักเกณฑ์ของทางธนาคาร</small></div>
      <div class="text-secondary"><small>-
          การคำนวณนี้เป็นวงเงินที่ประมาณการยอดเงินกู้ได้สูงสุดซึ่งขึ้นอยู่กับรายได้</small></div>
    </div>

    <div class="row g-3" v-if="calculated">
      <div class="col-md-8">
        <h3 class="fw-bold">โครงการที่แนะนำ</h3>
        <div class="text-muted">
          ทั้งหมด <b>{{ filteredProjects.length }}</b> โครงการ
          • บ้านเดี่ยว <b>{{ filteredSingleHouse.length }}</b>
          • ทาวน์โฮม <b>{{ filteredTownhome.length }}</b>
        </div>
      </div>
      <div class="col-md-2 align-self-center">
        <div class="mb-3 text-end"><small>เรียงลำดับ</small></div>
      </div>
      <div class="col-md-2 text-end">
        <select class="form-select form-select-sm" v-model="sortOrder" @change="sortProjects" aria-label="Sort">
          <option value="1">ราคาน้อย - มาก</option>
          <option value="2">ราคามาก - น้อย</option>
        </select>
      </div>
    </div>

    <!-- กลุ่มบ้านเดี่ยว -->
    <div v-if="calculated && filteredSingleHouse.length > 0" class="mt-4">
      <h4 class="fw-bold mb-3">บ้านเดี่ยว</h4>
      <div class="row">
        <div class="col-12 col-sm-6 col-md-6 col-lg-3" v-for="project in filteredSingleHouse" :key="`sh-${project.id}`">
          <a :href="project.project_url" target="_blank" class="text-decoration-none">
            <div class="card border-0 mb-3 singlehouse-card">
              <!-- รูปเต็ม -->
              <img :src="project.image" class="card-img-top project-img" alt="image" />

              <!-- ส่วนข้อมูล -->
              <div class="card-body project-info d-flex justify-content-between align-items-center text-white">
                <div>
                  <p class="mb-0">{{ project.brand }}</p>
                  <div class="location mb-0">{{ project.location }}</div>
                </div>

                <div v-if="asNumber(project.list_price_min_million) > 0" class="text-end">
                  <small>ราคาเริ่มต้น (ลบ.)</small><br>
                  <b>{{ priceLabel(project) }}</b>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>

    <!-- กลุ่มทาวน์โฮม -->
    <div v-if="calculated && filteredTownhome.length > 0" class="mt-4">
      <h4 class="fw-bold mb-3">ทาวน์โฮม</h4>
      <div class="row">
        <div class="col-12 col-sm-6 col-md-6 col-lg-3" v-for="project in filteredTownhome" :key="`sh-${project.id}`">
          <a :href="project.project_url" target="_blank" class="text-decoration-none">
            <div class="card border-0 mb-3 singlehouse-card">
              <!-- รูปเต็ม -->
              <img :src="project.image" class="card-img-top project-img" alt="image" />

              <!-- ส่วนข้อมูล -->
              <div class="card-body project-info d-flex justify-content-between align-items-center text-white">
                <div>
                  <p class="mb-0">{{ project.brand }}</p>
                  <div class="mb-0 location">{{ project.location }}</div>
                </div>

                <div v-if="asNumber(project.list_price_min_million) > 0" class="text-end">
                  <small>ราคาเริ่มต้น (ลบ.)</small><br>
                  <b>{{ priceLabel(project) }}</b>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>

    <!-- ถ้าไม่มีทั้งสองกลุ่ม -->
    <div v-if="calculated && filteredProjects.length === 0" class="text-center text-muted my-5">
      ไม่พบโครงการที่อยู่ในงบประมาณของคุณ
    </div>
  </div>
</template>
<script>
import store from "@/store";
import { GET_BRITANIA_DETAILS } from "../store/actions.type";

export default {
  name: "LoanAmountCalculation",
  data() {
    return {
      monthlyIncome: "35,000",
      existingDebt: 0,
      loanTerm: 30,
      loanAmount: null,          // ค่าจริงหลังคำนวณ
      displayLoanAmount: 0,      // ค่าที่ใช้แสดงผล (นับจาก 0 → loanAmount)
      animating: false,
      calculated: false,         // ใช้ควบคุมการโชว์ “โครงการที่แนะนำ”
      errorMessage: "",
      projects: [],
      sortOrder: "2",
      interestRateBase: 5,
    };
  },
  async created() { await this.init(); },
  computed: {
    // ทั้งหมดที่เข้าเงื่อนไขงบ
    filteredProjects() {
      if (this.loanAmount === null) return [];
      const withinBudget = this.projects.filter(p => {
        const minM = this.asNumber(p.list_price_min_million);
        if (minM == null) return false;
        return minM * 1_000_000 <= this.loanAmount;
      });
      return this.sortByMinPrice(withinBudget);
    },
    // บ้านเดี่ยว
    filteredSingleHouse() {
      return this.filteredProjects.filter(p => (p.project_type || '').toLowerCase() === 'single_house');
    },
    // ทาวน์โฮม
    filteredTownhome() {
      return this.filteredProjects.filter(p => (p.project_type || '').toLowerCase() === 'townhome');
    },
  },
  methods: {
    async init() {
      const rows = await store.dispatch(GET_BRITANIA_DETAILS);
      this.projects = (rows || []).map(r => ({
        id: Number(r.id),
        brand: r.brand || "",
        location: r.location || "",
        list_price_min_million: this.asNumber(r.list_price_min_million),
        list_price_max_million: this.asNumber(r.list_price_max_million),
        project_url: r.project_url || "",
        image: r.image || r.image_url || "",
        project_type: (r.project_type || "").toLowerCase(), // 'single_house' | 'townhome'
      }));
    },
    // เรียงตามราคา min ตาม sortOrder
    sortByMinPrice(arr) {
      const copy = [...arr];
      copy.sort((a, b) => {
        const aMin = this.asNumber(a.list_price_min_million) ?? Infinity;
        const bMin = this.asNumber(b.list_price_min_million) ?? Infinity;
        return this.sortOrder === "1" ? aMin - bMin : bMin - aMin;
      });
      return copy;
    },
    // ===== คำนวณวงเงินกู้ =====
    calculateLoanAmount() {
      const monthlyIncomeNum = this.parseNumber(this.monthlyIncome);
      const existingDebtNum = Number(this.existingDebt);

      if (isNaN(monthlyIncomeNum) || monthlyIncomeNum <= 0) { this.errorMessage = "กรุณาใส่ รายได้ต่อเดือน ที่ถูกต้อง"; this.calculated = false; return; }
      if (isNaN(existingDebtNum) || existingDebtNum < 0) { this.errorMessage = "กรุณาใส่ ภาระหนี้ที่ต้องผ่อนชำระต่อเดือน"; this.calculated = false; return; }
      if (this.loanTerm <= 0 || this.loanTerm > 30) { this.errorMessage = "กรุณาใส่ ระยะเวลาที่ขอกู้ (สูงสุด 30 ปี)"; this.calculated = false; return; }

      this.errorMessage = "";
      const availableDebt = monthlyIncomeNum * 0.4 - existingDebtNum; // ความสามารถผ่อน/เดือน (บาท)
      if (availableDebt <= 0) {
        this.errorMessage = "รายได้ของคุณไม่สามารถรองรับการผ่อนชำระได้";
        this.loanAmount = null; this.displayLoanAmount = 0; this.calculated = false; return;
      }

      // ใช้ดอกเบี้ย 5% ต่อปี (เบื้องต้น)
      const annualRate = (this.interestRateBase || 5) / 100;
      const r = annualRate / 12;            // ดอกเบี้ยรายเดือน
      const n = this.loanTerm * 12;         // จำนวนงวด

      // คำนวณวงเงินกู้จากความสามารถผ่อน/เดือน
      const factor = r > 0 ? (1 - Math.pow(1 + r, -n)) / r : n; // กันหารศูนย์
      const target = availableDebt * factor;

      this.loanAmount = target;
      this.animateLoanCount(target); // ถ้ามีเอฟเฟกต์นับเลข
      this.calculated = true;
    },


    // ===== นับเลขจาก 0 → target ด้วย requestAnimationFrame =====
    animateLoanCount(target, duration = 800) {
      this.animating = true;
      this.displayLoanAmount = 0;
      const start = performance.now();

      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);       // 0..1
        const eased = 1 - Math.pow(1 - t, 3);                  // easeOutCubic
        this.displayLoanAmount = target * eased;
        if (t < 1) requestAnimationFrame(tick);
        else { this.displayLoanAmount = target; this.animating = false; }
      };
      requestAnimationFrame(tick);
    },
    // ===== helpers =====
    asNumber(v) {
      if (v === null || v === undefined) return null;
      const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^\d.-]/g, ""));
      return isNaN(n) ? null : n;
    },
    priceLabel(p) {
      const minM = this.asNumber(p.list_price_min_million);
      const maxM = this.asNumber(p.list_price_max_million);
      if (minM != null && maxM != null && maxM > minM) return `${minM.toFixed(2)} - ${maxM.toFixed(2)}`;
      return minM != null ? minM.toFixed(2) : "-";
    },
    formatWithComma(v) { return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, ","); },
    formatInput(e) {
      let value = e.target.value.replace(/[^\d]/g, "");
      value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      this.monthlyIncome = value;
    },
    parseNumber(v) { return parseFloat(String(v).replace(/[^\d.-]/g, "")); },
    sortProjects() { /* computed จะ re-run อัตโนมัติ */ },
    StartcalculateLoanAmount() {
      this.monthlyIncome = "45,000";
      this.existingDebt = 0;
      this.loanTerm = 30;
      this.loanAmount = null;
      this.displayLoanAmount = 0;
      this.animating = false;
      this.calculated = false;
      this.errorMessage = "";
    },
  }
};
</script>

<style scoped>
.singlehouse-card,
.project-img,
.project-info {
  border-radius: 0 !important;

}

.singlehouse-card {
  overflow: hidden;
}

.project-img {
  width: 100%;
  /* height: 220px; */
  height: auto;
  object-fit: cover;
  /* ทำให้รูปเต็มพอดี */
}

.project-info {
  background-color: #de504f;
  color: #fff;
  padding: 10px 12px;
}

.project-info p {
  font-size: 16px;
}

.project-info b {
  font-size: 16px;
}

.location {
  font-size: 18px;
  font-weight: 400;
}
</style>